import { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { RankingSelector } from './components/RankingSelector';
import { SidebarContentView } from './components/SidebarContentView';

import bg1x from '@/assets/tournaments/bg_friendcup-view.jpg';
import bg2x from '@/assets/tournaments/bg_friendcup-view@2x.jpg';
import { Loader } from '@/components';
import { Layout } from '@/components/GenericsTournaments';
import { PencilIcon } from '@/components/Navbar/Icons';
import useAuth from '@/hooks/useAuth';
import useFriendsCup from '@/hooks/useFriendsCup';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import { requestFCupViewTeam } from '@/services/friendsCup';
import { FCupViewTeamResponse, ListItemFCup } from '@/types/friendCup';
import { cn, GENERICS_WORDS } from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

export const ViewTeamFCup: React.FC = () => {
  const navigate = useNavigate();

  const { isDesktop, isMobile } = useIsDesktop();
  const { tourneyData, updateTourney } = useTourney();
  const { userInitialData: userLogged } = useAuth();
  const { handleSetGroup } = useFriendsCup();

  const { isLoading, data } = useQuery(
    'FCupViewTeam',
    async () => await requestFCupViewTeam(tourneyData.id),
    {
      onSuccess: async (response: FCupViewTeamResponse) => {
        if (response.estado) {
          handleSetGroup({
            nombre: response?.nombreGrupo ?? '',
            idGrupo: tourneyData.id || 0,
            estado: true,
          });
          updateTourney({
            tourney: response,
            isOwner: Boolean(
              response.participantesGrupo &&
                response.participantesGrupo.find(
                  (item) => item.idUsuario === userLogged?.idUsuarioDT
                )
            ),
            tourneyGuests: response.pendientesAceptarPorInvitado,
            tourneyPlayers: response.participantesGrupo,
            tourneyPendings: response.queAunNoAceptaste,
          });
        } else {
          console.error(
            `Error obteniendo datos de ${GENERICS_WORDS.friendsCup}.`
          );
        }
      },
      onError: (error) => {
        console.error(
          `Error obteniendo datos de ${GENERICS_WORDS.friendsCup}.`,
          error
        );
        throw new Error('Invalid response state');
      },
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      enabled: Boolean(tourneyData.id),
    }
  );

  const tourney: FCupViewTeamResponse = useMemo(() => {
    return tourneyData.tourney;
  }, [tourneyData]);

  const setIsOwner = useCallback(() => {
    const idOwner = tourneyData.tourneysPlaying.find(
      (item: ListItemFCup) => item.idUsuarioCreador
    );
    const group: FCupViewTeamResponse = tourneyData.tourney;

    updateTourney({
      isOwner: Boolean(
        group?.participantesGrupo &&
          group.participantesGrupo.find((item) => item.idUsuario === idOwner)
      ),
    });
  }, [updateTourney, tourneyData]);

  useEffect(() => {
    setIsOwner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // manejo redireccion a administrar (mobile)
  const handleEditClick = useCallback(() => {
    navigate(ROUTES.friendsCup.adminTeam);
  }, [navigate]);

  const subtitle = useMemo(() => {
    return tourney?.fechaEqIncompleto ? (
      <h2 className="font-medium lg:font-semibold text-sm text-gdt-gray-2 mt-5">
        Recordá que si tu equipo no está completo,{' '}
        <strong>no sumará puntos.</strong>
      </h2>
    ) : (
      <h2 className="font-medium lg:font-semibold text-sm text-gdt-gray-2 mt-5">
        Tu equipo suma con los puntos de tus 11 y el de tus 3 amigos.
      </h2>
    );
  }, [tourney?.fechaEqIncompleto]);

  return (
    <>
      <Loader isVisible={isLoading} timeout={isLoading} />
      <Layout
        adsSections={SECTION_NAME_FOR_ADS.copadeamigos}
        backButton={isDesktop && true}
        backButtonAction={() => navigate(ROUTES.friendsCup.home)}
        className="px-4 mt-4"
        coverTop={() =>
          isDesktop && (
            <figure className="overflow-hidden rounded-lg">
              <img
                alt={GENERICS_WORDS.friendsCup}
                className="w-full"
                decoding="async"
                loading="lazy"
                src={bg1x}
                srcSet={`${bg1x} 1x, ${bg2x} 2x`}
              />
            </figure>
          )
        }
        main={() => (
          <div className="lg:pr-4">
            {isMobile && subtitle}
            {isDesktop && (
              <div
                className={cn('max-h-[190px] h-screen', {
                  'max-h-[110px]':
                    tourneyData.isOwner &&
                    !tourney?.cantSolPendientes &&
                    tourney?.fechasCmbVerEquipo2,
                })}
              >
                <h1 className="font-bold text-2xl">{tourney?.nombreGrupo}</h1>
                {subtitle}
              </div>
            )}
            <div className="lg:bg-white lg:rounded-xl overflow-hidden">
              <RankingSelector />
            </div>
          </div>
        )}
        mobile={{
          leftContent: {
            backAction: () => {
              navigate(ROUTES.friendsCup.home);
            },
          },
          centerContent: {
            title: data?.nombreGrupo,
          },
          rightContent: {
            customRightContent: tourneyData.isOwner &&
              tourney?.fechaEqIncompleto && (
                <button
                  className="rounded-[.63rem] bg-gdt-gray-9 w-8 h-8 flex justify-center items-center"
                  onClick={handleEditClick}
                >
                  <PencilIcon />
                </button>
              ),
          },
        }}
        sidebar={() => isDesktop && <SidebarContentView />}
      />
    </>
  );
};
